import { FormulaDisplay } from '@/components/Modals/EditDataTableFormulaColumn/FormulaDisplay';
import { DateRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { capitalise, formatNumber } from '@/helpers';
import { _isNil, _isNotEmpty, _notNil, uuid } from '@/littledash';
import { formatDuration } from 'date-fns';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { RiArrowDropDownFill } from 'react-icons/ri';
import { parse as parseDuration } from 'tinyduration';
import cn from 'classnames';
import { Dropdown } from '../../Dropdown';
import ColumnMenu from './ColumnMenu';
import type {
  DataTableCellSelectionChangeEvent,
  DataTableColumn,
  DataTableStateChangeEvent,
  DataTableType,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { useDataTable } from '../DataTableProvider';
import { DataTableColumnTypeSymbol } from './DataTableColumnTypeSymbol';

interface DataTableColumnHeaderProps {
  columnIndex: number;
  positionOffset: { x: number; y: number };
}

interface DataTableTruncateWrapperProps {
  children: ReactNode;
  tooltip: string;
}

export const DataTableColumnHeader = ({ columnIndex, positionOffset }: DataTableColumnHeaderProps) => {
  const { dataTableService } = useDataTable();
  const [columnUpdate, triggerColumnUpdate] = useState('');
  const [rowCount, updateRowCount] = useState(dataTableService.rows.length);
  const column = useMemo(
    () => dataTableService.columnByIndex(columnIndex),
    [columnIndex, dataTableService, columnUpdate]
  );
  const [columnSelected, setColumnSelected] = useState(false);

  useEffect(() => {
    if (_notNil(dataTableService) && _notNil(columnIndex)) {
      const destroy = new AbortController();
      setColumnSelected(dataTableService.columnSelected(columnIndex));
      dataTableService.subscribe(
        DataTableEvent.TableStateChange,
        (event: DataTableStateChangeEvent) => {
          if (event.detail.type.has('rows')) {
            updateRowCount(dataTableService.rows.length);
          }
          if (event.detail.type.has('columns') && _notNil(column) && event.detail.ids.has(column?.id)) {
            triggerColumnUpdate(uuid());
          }
        },
        destroy.signal
      );
      dataTableService.subscribe(
        DataTableEvent.CellSelectionChange,
        (event: DataTableCellSelectionChangeEvent) => {
          setColumnSelected((previous) => {
            const current = event.detail.selection.containsColumn(columnIndex);
            return previous === current ? previous : current;
          });
        },
        destroy.signal
      );
      return () => destroy.abort('teardown');
    }
  }, [column, columnIndex, dataTableService]);

  const { cellWidth, columnHeight } = dataTableService.dimensions;
  const hasGroupBorder = dataTableService.rightBorderType(columnIndex) === 'group-separator';

  return (
    <div
      className={cn(styles.columnHeaderContainer, { [styles.groupBorder]: hasGroupBorder })}
      data-test-component="DataTableColumnHeader"
      data-test-element="container"
      key={column?.id}
      onClick={() => dataTableService.selectColumn(columnIndex)}
      style={{
        position: 'absolute',
        top: positionOffset.y,
        left: positionOffset.x,
        width: cellWidth,
        height: columnHeight,
      }}
    >
      <div
        className={cn(styles.columnHeader, 'flex justify-between items-center', {
          [styles.selected]: columnSelected,
        })}
      >
        <div className={styles.columnType}>
          <DataTableColumnTypeSymbol
            type={column?.type ?? 'number'}
            locked={column?.locked ?? false}
            required={column?.type === 'metadata' && column.metadata?.required === true}
            tooltip={renderColumnTypeSymbolTooltip(column, dataTableService.leafColumns)}
          />

          <DataTaleColumnTitle tableType={dataTableService.type} column={column} />
        </div>
        <Dropdown
          disabled={dataTableService.readonly}
          renderMenu={() => <ColumnMenu column={column} dataTableService={dataTableService} rowCount={rowCount} />}
        >
          <div
            className={`${styles.columnHeaderDropdown} ${dataTableService.readonly ? 'ui__disabled' : ''}`}
            data-test-element="dropdown-toggle"
          >
            <RiArrowDropDownFill size={22} />
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

const CONTENT_MAX_WIDTH = 85;
const DataTableTruncateWrapper: FC<DataTableTruncateWrapperProps> = ({ children, tooltip }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const width = useRef<number>(0);
  useEffect(() => {
    if (ref.current) {
      width.current = ref?.current?.clientWidth ?? 0;
    }
  }, [ref]);

  return (
    <div ref={ref}>
      {(width?.current ?? 0) >= CONTENT_MAX_WIDTH ? (
        <Tooltip render={tooltip}>
          <span className={'data-table-truncate-wrapper'}>{children}</span>
        </Tooltip>
      ) : (
        <span className={'data-table-truncate-wrapper'}>{children}</span>
      )}
    </div>
  );
};

const renderColumnTypeSymbolTooltip = (
  column: Readonly<DataTableColumn> | undefined,
  columns: ReadonlyArray<DataTableColumn>
) => {
  switch (column?.type) {
    case 'formula':
      return FormulaDisplay({ formula: column?.formula, columns: columns });
    case 'timestampBaseline':
      return 'Timestamp: PK Dose';
    case 'timestampBaselineRelative':
      return 'Timestamp: PK Bleed';
    case 'sampleCollectedAt':
      return 'Collected At';
    case 'sampleDoseTime':
      return 'Dosed At';
    case 'samplePlannedAt':
      return 'Planned At';
    case 'sampleId':
      return 'ID';
    case 'sampleStatus':
      return 'Status';
    case 'sampleDetail':
      return column?.name;
    case 'sampleComments':
      return 'Comments';
    default:
      return capitalise(column?.type);
  }
};

interface DataTaleColumnTitleProps {
  tableType: DataTableType | 'unknown';
  column: DataTableColumn | undefined;
}

const DataTaleColumnTitle: FC<DataTaleColumnTitleProps> = ({ tableType, column }) => {
  switch (tableType) {
    case 'time':
      return <DataTableTimeColumnTitle name={column?.name ?? undefined} />;
    case 'numeric':
      return <DataTableNumericColumnTitle column={column} />;
    case 'custom':
    case 'sample':
      return <DataTableCustomColumnTitle column={column} />;
    default:
      return null;
  }
};

const DataTableTimeColumnTitle: FC<{ name: string | undefined }> = ({ name }) => {
  const title = useMemo(() => {
    if (_isNil(name)) {
      return null;
    }
    try {
      const duration = parseDuration(name);
      if (Object.values(duration).every((v) => v === 0)) {
        return '0';
      }
      return (duration.negative === true ? '- ' : '') + formatDuration(duration, { zero: false });
    } catch (err) {
      // Intentionally empty
    }
    return null;
  }, [name]);
  return <div className={styles.columnTextContainer}>{title ? <ColumnName name={String(title)} /> : ''}</div>;
};

const DataTableNumericColumnTitle: FC<{ column?: DataTableColumn }> = ({ column }) => {
  const name = column?.name;
  const unit = column && 'unit' in column ? column.unit : undefined;
  if (_isNil(name)) {
    return null;
  }
  const unitString = unit ?? '';
  return (
    <div className={styles.columnTextContainer}>
      <ColumnName name={String(formatNumber(name ?? ''))} />
      {_isNotEmpty(unitString) && <ColumnUnit unit={unitString} />}
    </div>
  );
};

const DataTableCustomColumnTitle: FC<Pick<DataTaleColumnTitleProps, 'column'>> = ({ column }) => {
  if (_isNil(column)) {
    return null;
  }
  switch (column.type) {
    case 'timestamp':
    case 'timestampBaseline':
    case 'timestampBaselineRelative':
    case 'text':
    case 'sample':
    case 'metadata':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name={column.name ?? ''} />
        </div>
      );
    case 'formula':
    case 'number': {
      const unit = column.unit?.trim() ?? '';
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name={column.name ?? ''} />
          {_isNotEmpty(unit) && <ColumnUnit unit={unit} />}
        </div>
      );
    }
    case 'measurement':
    case 'observation':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnDate date={column?.reference_date ?? ''} />
          <div className="flex flex-row">
            <ColumnName
              name={column.name ?? ''}
              unit={column.type === 'measurement' ? (column.unit ?? undefined) : undefined}
            />
          </div>
        </div>
      );
    case 'sampleDoseTime':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name="Dosed at" />
        </div>
      );
    case 'sampleId':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name="ID" />
        </div>
      );
    case 'sampleStatus':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name="Status" />
        </div>
      );
    case 'samplePlannedAt':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name="Planned at" />
        </div>
      );
    case 'sampleCollectedAt':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name="Collected at" />
        </div>
      );
    case 'sampleDetail':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name={column.name} unit={column.unit} />
        </div>
      );
    case 'sampleComments':
      return (
        <div className={styles.columnTextContainer}>
          <ColumnName name="Comments" />
        </div>
      );
    default:
      return null;
  }
};

const ColumnName: FC<{ name: string; unit?: string }> = ({ name, unit }) => {
  return (
    <DataTableTruncateWrapper tooltip={name}>
      <div className={styles.columnName}>
        {name}
        {_notNil(unit) && (
          <span className={styles.columnUnit} style={{ marginLeft: '5px' }}>
            ({unit.trim()})
          </span>
        )}
      </div>
    </DataTableTruncateWrapper>
  );
};

const ColumnUnit: FC<{ unit: string }> = ({ unit }) => {
  return (
    <DataTableTruncateWrapper tooltip={unit}>
      <div className={styles.columnUnit}>({String(unit).trim()})</div>
    </DataTableTruncateWrapper>
  );
};

const ColumnDate: FC<{ date: string }> = ({ date }) => (
  <div className={`fw4 ${styles.columnDate}`}>
    <DateRenderer value={date} defaultResponse="" />
  </div>
);
